// Alici mesajlari: siparis/kargo/teslim mesajlari ve 4-5 yildizli yorum yanitlari.
//
// Etsy'de mesaj ve yorum yaniti API'si yok. Tek API yolu kargo bildirimine
// eklenen not: "kargoya verildi" taslagi takip numarasiyla burada (veya
// Siparisler'de) onaylanip gercekten gonderilir. Digerlerini onaylayinca
// eklenti Etsy sekmesinde yazip gonderir; eklenti yoksa kopyala + Etsy'de ac.

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  MessageCircle, Loader2, RefreshCw, Save, Copy, ExternalLink, Check, X,
  Star, Truck, Package, PackageCheck, Info, RotateCcw, ChevronDown, ChevronUp, Send,
} from "lucide-react";
import { clsx } from "clsx";
import { useActiveShopId } from "../store/shop.store";
import { pingExtension } from "../lib/extension";
import { rememberCarrier } from "../lib/carriers";
import TrackingFields from "../components/orders/TrackingFields";
import {
  etsyComposeUrl, getBuyerMessagingConfig, listBuyerMessages, saveBuyerMessagingConfig,
  scanBuyerMessages, sendViaExtension, shipWithNote, updateBuyerMessage,
  type BuyerMessage, type BuyerMessagingConfig, type FlowKind,
} from "../lib/buyer-messages";

const FLOWS: Array<{ kind: FlowKind; title: string; desc: string; icon: typeof Package }> = [
  { kind: "order_received", title: "Sipariş geldiğinde", desc: "Yeni ödenmiş siparişe teşekkür mesajı. Onaylayınca eklenti Etsy'de gönderir.", icon: Package },
  { kind: "order_shipped", title: "Kargoya verildiğinde", desc: "Takip numarası panelden girilince Etsy'nin kargo bildirimiyle otomatik gider (API).", icon: Truck },
  { kind: "order_delivered", title: "Teslim edildiğinde", desc: "Teslimden sonra memnuniyet + yorum ricası. Onaylayınca eklenti Etsy'de gönderir.", icon: PackageCheck },
  { kind: "review_reply", title: "Yorum yanıtı", desc: "4-5 yıldızlı yorumlara herkese açık teşekkür yanıtı. Onaylayınca eklenti Etsy'de yayınlar.", icon: Star },
];
const FLOW_BY_KIND = Object.fromEntries(FLOWS.map((f) => [f.kind, f])) as Record<FlowKind, (typeof FLOWS)[number]>;

const PLACEHOLDERS = "{buyer_first_name} · {shop_name} · {item} · {tracking} — birden fazla varyant için araya tek satır --- koy";

const FILL_ONLY_KEY = "erc_buyer_msg_fill_only";

const TABS = [
  { key: "draft", label: "Taslaklar" },
  { key: "sent", label: "Gönderilenler" },
  { key: "failed", label: "Hatalı" },
  { key: "dismissed", label: "Yoksayılanlar" },
] as const;

function fmtDate(iso: string | null) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("tr-TR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

function Toggle({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={() => onChange(!on)}
      className={clsx("relative h-6 w-11 shrink-0 rounded-full transition-colors", on ? "bg-emerald-500" : "bg-gray-300")}
    >
      <span className={clsx("absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all", on ? "left-[22px]" : "left-0.5")} />
    </button>
  );
}

// ---------------- Ayarlar ----------------

/** Hazir cevaplar: alicinin sorusuna elle gonderilen kalip metinler (kopyala) */
function ReplyTemplatesCard() {
  const shopId = useActiveShopId();
  const { data } = useQuery({ queryKey: ["buyer-messaging-config", shopId], queryFn: getBuyerMessagingConfig });
  const [copied, setCopied] = useState<string | null>(null);
  const replies = data?.replies ?? [];
  if (!replies.length) return null;
  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <div className="border-b border-gray-100 px-5 py-4">
        <h2 className="font-semibold text-gray-900">Hazır cevaplar</h2>
        <p className="mt-0.5 text-xs text-gray-500">Alıcının sorusuna Etsy'de elle yapıştır; {"{buyer_first_name}"} yerine alıcının adını yaz.</p>
      </div>
      <div className="divide-y divide-gray-100">
        {replies.map((r) => (
          <div key={r.key} className="px-5 py-3">
            <div className="mb-1.5 flex items-center justify-between gap-3">
              <p className="text-sm font-medium text-gray-800">{r.label}</p>
              <button
                onClick={() => { void navigator.clipboard.writeText(r.body); setCopied(r.key); setTimeout(() => setCopied(null), 1500); }}
                className="flex items-center gap-1 rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50"
              >
                {copied === r.key ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                {copied === r.key ? "Kopyalandı" : "Kopyala"}
              </button>
            </div>
            <p className="whitespace-pre-line text-xs text-gray-600">{r.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsCard() {
  const qc = useQueryClient();
  const shopId = useActiveShopId();
  const { data, isLoading } = useQuery({ queryKey: ["buyer-messaging-config", shopId], queryFn: getBuyerMessagingConfig });
  const [cfg, setCfg] = useState<BuyerMessagingConfig | null>(null);
  const [open, setOpen] = useState<FlowKind | null>(null);
  useEffect(() => { if (data) setCfg(data.data); }, [data]);

  const dirty = useMemo(() => !!cfg && !!data && JSON.stringify(cfg) !== JSON.stringify(data.data), [cfg, data]);
  const save = useMutation({
    mutationFn: async (c: BuyerMessagingConfig) => {
      const saved = await saveBuyerMessagingConfig(c);
      // Yeni acilan akislar hemen taslak uretsin
      await scanBuyerMessages().catch(() => 0);
      return saved;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["buyer-messaging-config"] });
      qc.invalidateQueries({ queryKey: ["buyer-messages"] });
    },
  });

  if (isLoading || !cfg || !data) {
    return <div className="flex justify-center rounded-xl border border-gray-200 bg-white p-8"><Loader2 className="h-6 w-6 animate-spin text-orange-500" /></div>;
  }

  const setFlow = (k: FlowKind, patch: Partial<BuyerMessagingConfig["flows"][FlowKind]>) =>
    setCfg({ ...cfg, flows: { ...cfg.flows, [k]: { ...cfg.flows[k], ...patch } } });
  const sinceDays = (d: number) => setCfg({ ...cfg, since: new Date(Date.now() - d * 86400_000).toISOString() });

  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-5 py-4">
        <div>
          <h2 className="font-semibold text-gray-900">Otomatik mesajlar</h2>
          <p className="text-xs text-gray-500">
            Başlangıç: <b>{new Date(cfg.since).toLocaleString("tr-TR")}</b> — bundan önceki sipariş olaylarına ve yorumlara taslak üretilmez
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-gray-500">Başlangıcı ayarla:</span>
          {[{ l: "Şimdi", d: 0 }, { l: "7 gün önce", d: 7 }, { l: "30 gün önce", d: 30 }].map((o) => (
            <button key={o.l} onClick={() => sinceDays(o.d)} className="rounded-md border border-gray-200 px-2 py-1 text-gray-600 hover:bg-gray-50">{o.l}</button>
          ))}
        </div>
      </div>

      <div className="divide-y divide-gray-100">
        {FLOWS.map((f) => {
          const flow = cfg.flows[f.kind];
          const Icon = f.icon;
          const expanded = open === f.kind;
          return (
            <div key={f.kind} className="px-5 py-3">
              <div className="flex items-center gap-3">
                <Icon className={clsx("h-5 w-5 shrink-0", flow.enabled ? "text-orange-500" : "text-gray-300")} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900">{f.title}</p>
                  <p className="text-xs text-gray-500">{f.desc}</p>
                </div>
                {f.kind === "review_reply" && (
                  <select
                    value={cfg.minRating}
                    onChange={(e) => setCfg({ ...cfg, minRating: Number(e.target.value) })}
                    className="rounded-md border border-gray-200 px-2 py-1 text-xs"
                  >
                    <option value={4}>4-5 yıldız</option>
                    <option value={5}>Yalnız 5 yıldız</option>
                  </select>
                )}
                <button
                  onClick={() => setOpen(expanded ? null : f.kind)}
                  className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-gray-600 hover:bg-gray-50"
                >
                  Şablon {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                </button>
                <Toggle on={flow.enabled} onChange={(v) => setFlow(f.kind, { enabled: v })} />
              </div>
              {expanded && (
                <div className="mt-3 pl-8">
                  <textarea
                    value={flow.template}
                    onChange={(e) => setFlow(f.kind, { template: e.target.value })}
                    rows={f.kind === "review_reply" ? 9 : 7}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 font-mono text-xs leading-relaxed"
                  />
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <p className="text-[11px] text-gray-400">{PLACEHOLDERS}</p>
                    <button
                      onClick={() => setFlow(f.kind, { template: data.defaults[f.kind] })}
                      className="flex shrink-0 items-center gap-1 text-[11px] text-gray-500 hover:text-gray-800"
                    >
                      <RotateCcw className="h-3 w-3" /> Varsayılan
                    </button>
                  </div>
                  {f.kind === "order_received" && (
                    <div className="mt-4">
                      <p className="mb-1 block text-xs font-medium text-gray-700">Birden fazla ürün varsa</p>
                      <textarea
                        value={flow.multiTemplate ?? data.defaults.order_received_multi ?? ""}
                        onChange={(e) => setFlow(f.kind, { multiTemplate: e.target.value })}
                        rows={7}
                        aria-label="Birden fazla ürünlü sipariş mesajı şablonu"
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 font-mono text-xs leading-relaxed"
                      />
                      <div className="mt-1 flex items-center justify-between gap-2">
                        <p className="text-[11px] text-gray-500">{"{item_count}"} toplam adet · {"{items}"} her ürün bir satır (beden/renk ile)</p>
                        <button
                          onClick={() => setFlow(f.kind, { multiTemplate: data.defaults.order_received_multi })}
                          className="flex shrink-0 items-center gap-1 text-[11px] text-gray-500 hover:text-gray-800"
                        >
                          <RotateCcw className="h-3 w-3" /> Varsayılan
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-end gap-3 border-t border-gray-100 px-5 py-3">
        {save.isError && <p className="text-xs text-red-600">{(save.error as Error).message}</p>}
        {save.isSuccess && !dirty && <p className="text-xs text-emerald-600">Kaydedildi</p>}
        <button
          onClick={() => save.mutate(cfg)}
          disabled={!dirty || save.isPending}
          className="flex items-center gap-1.5 rounded-lg bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-40"
        >
          {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Kaydet
        </button>
      </div>
    </div>
  );
}

// ---------------- Taslak karti ----------------

function MessageCard({ m, extReady, fillOnly }: { m: BuyerMessage; extReady: boolean; fillOnly: boolean }) {
  const qc = useQueryClient();
  const [body, setBody] = useState(m.body);
  const [carrier, setCarrier] = useState("");
  const [code, setCode] = useState("");
  const [opened, setOpened] = useState(false);
  const [extNote, setExtNote] = useState<string | null>(null);
  const flow = FLOW_BY_KIND[m.kind];
  const Icon = flow.icon;
  const editable = m.status === "draft" || m.status === "failed";

  const refresh = () => qc.invalidateQueries({ queryKey: ["buyer-messages"] });
  const patch = useMutation({
    mutationFn: (p: Parameters<typeof updateBuyerMessage>[1]) => updateBuyerMessage(m.id, p),
    onSuccess: refresh,
  });
  const ship = useMutation({
    mutationFn: () => {
      rememberCarrier(carrier);
      return shipWithNote(m.receiptId!, { tracking_code: code.trim(), carrier_name: carrier.trim(), note_to_buyer: body.trim(), buyer_message_id: m.id });
    },
    onSuccess: () => { refresh(); qc.invalidateQueries({ queryKey: ["etsy-receipts"] }); },
  });

  // Eklenti: Etsy sekmesinde yazar ve gonderir (fillOnly ise yalniz doldurur)
  const viaExt = useMutation({
    mutationFn: async () => {
      if (m.status === "failed" && /doğrulanamadı/i.test(m.error || "") &&
          !window.confirm("Önceki deneme doğrulanamamıştı; mesaj gitmiş olabilir. Yine de tekrar gönderilsin mi?")) return;
      setExtNote(null);
      const r = await sendViaExtension(m, body, fillOnly);
      if (!r) throw new Error("Eklentiye ulaşılamadı — eklentiyi yenile ya da Kopyala & Etsy'de aç kullan.");
      if (r.ok && r.sent) await updateBuyerMessage(m.id, { status: "sent", body });
      else if (r.ok) { setOpened(true); setExtNote("Etsy sekmesinde dolduruldu — Gönder'e bastıktan sonra burada Gönderildi işaretle."); }
      else await updateBuyerMessage(m.id, { status: "failed", error: r.error, body });
    },
    onSuccess: refresh,
  });

  const copyAndOpen = async () => {
    try { await navigator.clipboard.writeText(body); } catch { /* kopyalanamadiysa metin kutuda duruyor */ }
    if (body !== m.body) patch.mutate({ body });
    window.open(etsyComposeUrl(m), "_blank", "noopener");
    setOpened(true);
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
        <span className="flex items-center gap-1 rounded-full bg-orange-50 px-2 py-0.5 font-medium text-orange-700">
          <Icon className="h-3.5 w-3.5" /> {flow.title}
        </span>
        {m.channel === "api" && m.status === "draft" && (
          <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-medium text-emerald-700">API ile gider</span>
        )}
        {m.buyerName && <span className="font-medium text-gray-800">{m.buyerName}</span>}
        {m.receiptId && <span className="text-gray-400">#{m.receiptId}</span>}
        {m.listingTitle && <span className="max-w-xs truncate text-gray-500" title={m.listingTitle}>{m.listingTitle}</span>}
        <span className="ml-auto text-gray-400">{fmtDate(m.status === "sent" ? m.sentAt : m.eventAt)}</span>
      </div>

      {m.kind === "review_reply" && (
        <div className="mb-2 rounded-lg bg-gray-50 px-3 py-2 text-sm">
          <div className="mb-1 flex gap-0.5">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star key={i} className={clsx("h-3.5 w-3.5", i <= (m.rating ?? 0) ? "fill-yellow-400 text-yellow-400" : "text-gray-200")} />
            ))}
          </div>
          <p className="text-gray-700">{m.reviewText || <i className="text-gray-400">Yorum metni yok (yalnız yıldız)</i>}</p>
        </div>
      )}

      {editable ? (
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={Math.min(10, body.split("\n").length + 1)}
          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm leading-relaxed"
        />
      ) : (
        <p className="whitespace-pre-wrap rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700">{m.body}</p>
      )}
      {m.error && <p className="mt-1 text-xs text-red-600">{m.error}</p>}
      {(patch.isError || ship.isError || viaExt.isError) && (
        <p className="mt-1 text-xs text-red-600">{((patch.error || ship.error || viaExt.error) as Error).message}</p>
      )}
      {extNote && <p className="mt-1 text-xs text-emerald-700">{extNote}</p>}

      {editable && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {m.channel === "api" ? (
            <>
              <TrackingFields compact code={code} carrier={carrier} onCode={setCode} onCarrier={setCarrier} />
              <button
                onClick={() => ship.mutate()}
                disabled={!carrier.trim() || !code.trim() || !body.trim() || ship.isPending}
                className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-40"
              >
                {ship.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Onayla: kargola + mesajı gönder
              </button>
            </>
          ) : (
            <>
              {extReady && (
                <button
                  onClick={() => viaExt.mutate()}
                  disabled={!body.trim() || viaExt.isPending}
                  className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-40"
                >
                  {viaExt.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {viaExt.isPending ? "Etsy'de gönderiliyor…" : fillOnly ? "Onayla: Etsy'de doldur" : "Onayla ve gönder"}
                </button>
              )}
              <button
                onClick={copyAndOpen}
                className={clsx(
                  "flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium",
                  extReady ? "border border-gray-200 text-gray-600 hover:bg-gray-50" : "bg-orange-500 text-white hover:bg-orange-600"
                )}
              >
                <Copy className="h-4 w-4" /> Kopyala & Etsy'de aç <ExternalLink className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => patch.mutate({ status: "sent", body })}
                disabled={patch.isPending}
                className={clsx(
                  "flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium",
                  opened ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-gray-200 text-gray-600 hover:bg-gray-50"
                )}
              >
                <Check className="h-4 w-4" /> Gönderildi
              </button>
            </>
          )}
          {body !== m.body && (
            <button onClick={() => patch.mutate({ body })} className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50">
              Metni kaydet
            </button>
          )}
          <button
            onClick={() => patch.mutate({ status: "dismissed" })}
            className="ml-auto flex items-center gap-1 rounded-lg px-2 py-1.5 text-sm text-gray-500 hover:bg-gray-50"
          >
            <X className="h-4 w-4" /> Yoksay
          </button>
        </div>
      )}
      {m.status === "dismissed" && (
        <button onClick={() => patch.mutate({ status: "draft" })} className="mt-2 text-xs text-gray-500 hover:text-gray-800">
          Taslaklara geri al
        </button>
      )}
    </div>
  );
}

// ---------------- Sayfa ----------------

export default function BuyerMessagesPage() {
  const qc = useQueryClient();
  const shopId = useActiveShopId();
  const [tab, setTab] = useState<(typeof TABS)[number]["key"]>("draft");
  const { data: extVersion = null } = useQuery({ queryKey: ["extension-ping"], queryFn: pingExtension, staleTime: 60_000 });
  const [fillOnly, setFillOnly] = useState(() => {
    try { return localStorage.getItem(FILL_ONLY_KEY) === "1"; } catch { return false; }
  });
  const toggleFillOnly = (v: boolean) => {
    setFillOnly(v);
    try { localStorage.setItem(FILL_ONLY_KEY, v ? "1" : "0"); } catch { /* yoksay */ }
  };

  // Siparisler satirindaki gorevden gelindiyse yalniz o siparisin mesajlari (tum durumlar)
  const [params, setParams] = useSearchParams();
  const receiptFilter = params.get("receipt");
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["buyer-messages", shopId, receiptFilter ?? tab],
    queryFn: () => listBuyerMessages(receiptFilter ? { receiptId: receiptFilter } : { status: tab }),
  });
  const scan = useMutation({
    mutationFn: scanBuyerMessages,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["buyer-messages"] }),
  });

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
            <MessageCircle className="h-6 w-6 text-orange-500" /> Alıcı Mesajları
          </h1>
          <p className="mt-1 text-sm text-gray-500">Sipariş, kargo, teslim mesajları ve yorum yanıtları — onaylamadan hiçbir şey gitmez</p>
        </div>
        <button
          onClick={() => scan.mutate()}
          disabled={scan.isPending}
          className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
        >
          <RefreshCw className={clsx("h-4 w-4", scan.isPending && "animate-spin")} /> Şimdi tara
        </button>
      </div>
      {scan.isSuccess && <p className="-mt-4 text-xs text-gray-500">{scan.data} yeni taslak</p>}
      {scan.isError && <p className="-mt-4 text-xs text-red-600">{(scan.error as Error).message}</p>}

      <div className="flex gap-2 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-xs text-blue-800">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="space-y-2">
          <p>
            <b>Kargo mesajı</b> takip numarasıyla Etsy'nin kargo bildiriminde API'den gider (burada ya da Siparişler'de).
            Etsy'nin API'si diğer mesajlara ve yorum yanıtına izin vermediği için onları <b>eklenti</b> Etsy sekmesinde
            yazıp gönderir (Etsy'de oturumun açık olmalı). Taslaklar her 20 dakikada bir üretilir.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            {extVersion ? (
              <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-medium text-emerald-800">Eklenti bağlı · v{extVersion}</span>
            ) : (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-800">
                Eklenti bağlı değil — Kopyala & Etsy'de aç kullanılır
              </span>
            )}
            {extVersion && (
              <label className="flex cursor-pointer items-center gap-1.5">
                <input type="checkbox" checked={fillOnly} onChange={(e) => toggleFillOnly(e.target.checked)} />
                Test: yalnız doldur, Gönder'e ben basayım
              </label>
            )}
          </div>
        </div>
      </div>

      <SettingsCard />
      <ReplyTemplatesCard />

      <div>
        {receiptFilter ? (
          <div className="mb-3 flex items-center gap-2 border-b border-gray-200 pb-2 text-sm">
            <span className="font-medium text-gray-700">Sipariş #{receiptFilter} mesajları</span>
            <button
              onClick={() => { params.delete("receipt"); setParams(params); }}
              className="flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-200"
            >
              <X className="h-3 w-3" /> Tümünü göster
            </button>
          </div>
        ) : (
        <div className="mb-3 flex gap-1 border-b border-gray-200">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={clsx(
                "-mb-px border-b-2 px-3 py-2 text-sm font-medium",
                tab === t.key ? "border-orange-500 text-orange-600" : "border-transparent text-gray-500 hover:text-gray-800"
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
        )}
        {isLoading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-orange-500" /></div>
        ) : rows.length === 0 ? (
          <p className="py-10 text-center text-sm text-gray-400">
            {tab === "draft" ? "Bekleyen taslak yok. Akışları açıp kaydet, ardından \"Şimdi tara\"." : "Kayıt yok."}
          </p>
        ) : (
          <div className="space-y-3">{rows.map((m) => <MessageCard key={m.id} m={m} extReady={!!extVersion} fillOnly={fillOnly} />)}</div>
        )}
      </div>
    </div>
  );
}
